import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/revenue-operations';

const RevenueOperationsLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Revenue Operations Hub | Explanatum';
    const desc = 'Track sales, monitor spending, and automate runway reporting in one workspace—without fragile spreadsheets or outsourced finance retainers.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Revenue operations overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-4 py-1 text-sm font-semibold text-sky-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                AI Revenue Operations Hub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Close the loop between sales in and cash going out.
                <span className="mt-2 block text-blue-600">
                  Forecast runway, margin, and spend from one command center.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Log deals with context, reconcile expenses instantly, and surface alerts when burn creeps up. Explanatum replaces brittle spreadsheets and finance consultants with a live view of revenue health for operators and founders.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_revenue_ops_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-emerald-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/finance"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open finance workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Structured sales logging with product, channel, and notes
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Expense intake with categories, vendors, and fixed-cost tagging
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                  Export-ready reports and alerts when burn thresholds hit
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Revenue dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Revenue pulse</span>
                  <span>Live metrics</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Week-to-date sales £42,900 • Top channel: Partnerships • Average deal size £2,860 • Notes auto surfaced for CS handoff.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Expense mix: 61% fixed, 39% variable • Vendor alerts flagged new SaaS spend • Burn rate trend down 8% vs last month.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Runway projection: 13.8 months • Consultant drafting board update with margin improvements and budget guardrails.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Revenue clarity from intake to board report</h2>
            <p className="mt-4 text-base text-gray-500">Stay ahead of burn, keep a live ledger of deals and spend, and give stakeholders the numbers they need instantly.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Unified sales logging',
              body: 'Capture product, channel, and context for every deal—no more chasing reps or digging through CRM comments.'
            }, {
              title: 'Expense governance',
              body: 'Tag vendors, categories, and fixed costs so finance and operations agree on what’s essential versus discretionary.'
            }, {
              title: 'Burn and runway tracking',
              body: 'See how inflows and outflows affect runway every week, with proactive alerts before issues escalate.'
            }, {
              title: 'Audit-ready exports',
              body: 'Generate CSVs and dashboards in seconds for investors, accountants, or leadership meetings.'
            }, {
              title: 'AI summaries & playbooks',
              body: 'Let the AI Business Consultant narrate trends, prepare board updates, and recommend budget adjustments.'
            }, {
              title: 'Team collaboration',
              body: 'Work from the same workspace—no version conflicts, no hidden assumptions, just transparent data everyone trusts.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why operators move off spreadsheets and outsourced finance</h2>
            <p className="mt-4 text-base text-gray-500">Keep pace with growth while maintaining financial discipline—without waiting on manual reconciliations or expensive consultants.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Revenue Operations Hub',
              points: [
                'Sales and expense intake with governance baked in',
                'Automatic runway, burn, and margin snapshots',
                'Alerts, AI narratives, and exports included in minutes',
                'Shared workspace for founders, finance, and GTM teams',
                'Predictable subscription that scales with usage'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Manual spreadsheets',
              points: [
                'Broken formulas and missing context across tabs',
                'Slow consolidation when teams submit updates late',
                'No automated alerting when burn spikes suddenly',
                'Hard to audit or share without version conflicts',
                'Hours lost turning numbers into stakeholder-ready summaries'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Finance consultants & fractional CFOs',
              points: [
                '£3,000+ monthly retainers for periodic reporting',
                'Feedback arrives days after the problem starts',
                'Insights locked behind external expertise and slides',
                'Limited visibility for internal teams without extra meetings',
                'Knowledge disappears when engagements end'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need board-ready revenue visibility by tomorrow?”</h3>
            <p className="mt-3 text-base text-gray-600">Log today’s deals and expenses, export clean reports, and let the consultant prepare the narrative—all inside one workspace.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_revenue_ops_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-emerald-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-sky-400/30 hover:scale-[1.02]"
              >
                Launch revenue ops now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where revenue operations discipline unlocks growth</h2>
            <p className="mt-4 text-base text-gray-500">Give founders, operators, and finance leads the same source of truth without waiting on month-end reconciliations.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Seed-stage startups',
              body: 'Monitor burn and runway weekly while capturing the detail investors ask about before the next raise.'
            }, {
              title: 'Scaling SaaS teams',
              body: 'Align GTM and finance with shared sales data, expense tagging, and automated margin updates.'
            }, {
              title: 'Agencies & studios',
              body: 'Track retainers, project revenue, and supplier costs together to keep utilisation and profitability on target.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default RevenueOperationsLanding;
